import React, { useEffect, useState } from 'react';
import { Heart, MessageCircle, Plus, Send, Image as ImageIcon, Clock3, CheckCircle2, XCircle } from 'lucide-react';
import { memberStoriesApi, uploadsApi } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';

const getApiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    const messages = detail.map((item) => item?.msg).filter(Boolean);
    return messages.length ? messages.join('. ') : fallback;
  }
  if (detail && typeof detail === 'object') return detail.msg || fallback;
  return typeof detail === 'string' && detail.trim() ? detail : fallback;
};

export default function MemberStories() {
  const [stories, setStories] = useState([]); const [myStories, setMyStories] = useState([]); const [title, setTitle] = useState(''); const [content, setContent] = useState(''); const [images, setImages] = useState([]); const [showEditor, setShowEditor] = useState(false); const [comment, setComment] = useState({}); const [saving, setSaving] = useState(false); const [activeTab, setActiveTab] = useState('community');
  const load = async () => { try { const [published, submitted] = await Promise.all([memberStoriesApi.list(), memberStoriesApi.list(true)]); setStories(published.data.stories || []); setMyStories(submitted.data.stories || []); } catch { toast.error('Failed to load member stories'); } };
  useEffect(() => { load(); }, []);
  const upload = async (files) => { try { const result = await Promise.all([...files].slice(0, 6).map(async f => (await uploadsApi.uploadFile(f, 'member_story')).data.file.url)); setImages(result); } catch (error) { toast.error(getApiErrorMessage(error, 'Unable to upload story images')); } };
  const submit = async () => { if (!title.trim() || !content.trim()) return toast.error('Title and story are required'); setSaving(true); try { await memberStoriesApi.submit({ title: title.trim(), content: content.trim(), image_urls: images }); toast.success('Story submitted for approval'); setTitle(''); setContent(''); setImages([]); setShowEditor(false); setActiveTab('mine'); await load(); } catch (error) { toast.error(getApiErrorMessage(error, 'Unable to submit story')); } finally { setSaving(false); } };
  const like = async (id) => { try { await memberStoriesApi.toggleLike(id); await load(); } catch (error) { toast.error(getApiErrorMessage(error, 'Unable to update the story reaction')); } };
  const addComment = async (id) => { if (!comment[id]?.trim()) return; try { await memberStoriesApi.comment(id, comment[id].trim()); setComment({...comment, [id]: ''}); await load(); } catch (error) { toast.error(getApiErrorMessage(error, 'Unable to add the comment')); } };
  const statusDetails = { pending: { label: 'Pending review', icon: Clock3, className: 'bg-amber-100 text-amber-800' }, approved: { label: 'Published', icon: CheckCircle2, className: 'bg-green-100 text-green-800' }, rejected: { label: 'Rejected', icon: XCircle, className: 'bg-red-100 text-red-800' } };
  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold">Member Success Stories</h1>
          <p className="text-gray-500">Celebrate business wins and learn from the network.</p>
        </div>
        <Button className="w-full shrink-0 sm:w-auto" onClick={() => setShowEditor(!showEditor)}>
          <Plus size={16} className="mr-2" />
          Share a Story
        </Button>
      </div>

      {showEditor && (
        <Card>
          <CardHeader><CardTitle>Share your success story</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Story title" maxLength={180} />
            <Textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Tell members about the achievement, route, partnership, or milestone..." className="min-h-40" />
            <label className="flex items-center gap-2 text-sm cursor-pointer"><ImageIcon size={16} />Add up to 6 images<input className="hidden" type="file" accept="image/*" multiple onChange={e => upload(e.target.files)} /></label>
            {images.length > 0 && <p className="text-sm text-green-700">{images.length} image(s) ready to submit</p>}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button className="w-full sm:w-auto" onClick={submit} disabled={saving}>{saving ? 'Submitting...' : 'Submit for Approval'}</Button>
              <Button className="w-full sm:w-auto" variant="outline" onClick={() => setShowEditor(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5">
        <div className="w-full">
          <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:inline-flex sm:h-9 sm:w-auto sm:gap-0">
            <TabsTrigger className="min-h-8 whitespace-normal py-2 text-center leading-tight sm:whitespace-nowrap sm:py-1" value="community">Community Posts</TabsTrigger>
            <TabsTrigger className="min-h-8 whitespace-normal py-2 text-center leading-tight sm:whitespace-nowrap sm:py-1" value="mine">My Story Submissions</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="community" className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold">Community Posts</h2>
            <p className="text-sm text-gray-500">Published stories from members across the network.</p>
          </div>
          {stories.length === 0 ? (
            <Card><CardContent className="py-16 text-center text-gray-500">No published stories yet. Be the first to share one.</CardContent></Card>
          ) : stories.map(s => (
            <Card key={s.story_id}>
              <CardHeader>
                <CardTitle className="break-words">{s.title}</CardTitle>
                <p className="break-words text-sm text-gray-500">{s.author_name}{s.author_company ? ` · ${s.author_company}` : ''}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="break-words whitespace-pre-wrap">{s.content}</p>
                {s.image_urls?.length > 0 && (
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                    {s.image_urls.map(url => <img key={url} src={url} alt="Story" className="aspect-video w-full rounded-md object-cover" />)}
                  </div>
                )}
                <div className="flex gap-3">
                  <Button variant="ghost" size="sm" onClick={() => like(s.story_id)}><Heart size={16} className={s.liked_by_me ? 'fill-red-500 text-red-500 mr-1' : 'mr-1'} />{s.likes_count || 0}</Button>
                  <span className="flex items-center text-sm text-gray-500"><MessageCircle size={16} className="mr-1" />{s.comments?.length || 0}</span>
                </div>
                {s.comments?.map(c => <p key={c.comment_id} className="break-words rounded bg-gray-50 p-2 text-sm"><b>{c.author_name}</b> {c.content}</p>)}
                <div className="flex gap-2">
                  <Input className="min-w-0 flex-1" value={comment[s.story_id] || ''} onChange={e => setComment({ ...comment, [s.story_id]: e.target.value })} placeholder="Write a comment" />
                  <Button className="shrink-0" size="icon" onClick={() => addComment(s.story_id)}><Send size={16} /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
        <TabsContent value="mine">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">My Story Submissions</CardTitle>
              <p className="text-sm text-gray-500">Track every story you have submitted and its review status.</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {myStories.length === 0 ? <p className="py-3 text-sm text-gray-500">You have not submitted a story yet.</p> : myStories.map(s => {
                const status = statusDetails[s.status] || statusDetails.pending;
                const StatusIcon = status.icon;
                return <div key={s.story_id} className="flex items-center justify-between gap-4 rounded-md border p-3"><div className="min-w-0"><p className="truncate font-medium">{s.title}</p><p className="text-xs text-gray-500">Submitted {new Date(s.created_at).toLocaleDateString()}</p></div><span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${status.className}`}><StatusIcon size={14} />{status.label}</span></div>;
              })}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
